import { ArrowUpIcon } from "lucide-react";
import { useCallback, useState, type FormEvent, type KeyboardEvent } from "react";

import { openCommandPalette } from "~/commandPaletteBus";
import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { useHandleNewThread } from "~/hooks/useHandleNewThread";
import { startNewThreadWithPrompt } from "~/lib/chatThreadActions";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { stackedThreadToast, toastManager } from "../ui/toast";

export function ActivitiesPromptBar({ className }: { readonly className?: string }) {
  const { t } = useI18n();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const threadContext = useHandleNewThread();
  const { isLoaded: clerkLoaded, isSignedIn, openSignIn } = useMorrowClerkAuth();

  const submit = useCallback(async () => {
    const text = draft.trim();
    if (!text || busy) return;

    if (clerkLoaded && !isSignedIn) {
      openSignIn();
      return;
    }

    setBusy(true);
    try {
      const started = await startNewThreadWithPrompt(
        { ...threadContext, activeThread: threadContext.activeThread ?? undefined },
        text,
      );
      if (!started) {
        toastManager.add(
          stackedThreadToast({
            type: "warning",
            title: t("activities.prompt.noProjectTitle"),
            description: t("activities.prompt.noProjectDescription"),
          }),
        );
        openCommandPalette({ open: "add-project" });
        return;
      }
      setDraft("");
    } finally {
      setBusy(false);
    }
  }, [busy, clerkLoaded, draft, isSignedIn, openSignIn, t, threadContext]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className={cn(
        "relative flex w-full min-w-0 items-center sm:min-w-[14rem] sm:max-w-md",
        className,
      )}
    >
      <input
        type="text"
        value={draft}
        onChange={(event) => setDraft(event.currentTarget.value)}
        onKeyDown={onKeyDown}
        placeholder={t("activities.prompt.placeholder")}
        disabled={busy}
        className="h-9 w-full rounded-full border border-border bg-muted/30 py-0 pe-11 ps-3.5 text-sm text-foreground shadow-xs/5 outline-none placeholder:text-muted-foreground focus-visible:border-primary/40 focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
      />
      <button
        type="submit"
        disabled={!draft.trim() || busy}
        aria-label={t("activities.prompt.send")}
        className="absolute end-1 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full border border-border/60 bg-background text-foreground shadow-xs transition hover:bg-muted/50 disabled:opacity-35"
      >
        <ArrowUpIcon className="size-4" strokeWidth={2.25} />
      </button>
    </form>
  );
}
